import { CommonModule, DecimalPipe } from '@angular/common';
import {
  Component,
  DestroyRef,
  EventEmitter,
  HostListener,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  GoogleMapsModule,
  MapInfoWindow,
  MapMarkerClusterer,
} from '@angular/google-maps';
import { ActivatedRoute } from '@angular/router';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { debounceTime, map, take, tap } from 'rxjs/operators';
import {
  PreApprovalStatus,
  PropertyListForMapResponse,
  PropertySummary,
} from 'src/app/services/portal-api.service';
import { MapInfoWindowComponent } from './map-info-window/map-info-window.component';
import {
  PropertyListFilterWithPagination,
  PropertyListingService,
} from 'src/app/services/property.service';
import { FindPropertyService } from 'src/app/core/services/find-property.service';

export class marker {
  icon: string | google.maps.Icon | any;
  title: string | google.maps.MarkerLabel | any;
  id?: string | any;
  content?: any | any;
  position: google.maps.LatLng | any;
  label:
    | { text: string; color: string; fontSize: string; fontWeight: string }
    | undefined;
  // details:
  //   | {
  //       id: string | undefined;
  //       code: string;
  //       title: string | undefined;
  //       prefecture: string | undefined;
  //       price: number | string | undefined;
  //       propertyArea: number | string | undefined;
  //       isExclusive: boolean;
  //       isFavorite: boolean;
  //       isHot: boolean;
  //       isPromoted: boolean;
  //       isSold: boolean;
  //       isSponsored: boolean;
  //       auctionRound: number | string | undefined;
  //       preApproved: PreApprovalStatus | undefined;
  //     }
  //   | undefined;
}

@Component({
  selector: 'app-map',
  standalone: true,
  imports: [CommonModule, GoogleMapsModule, MapInfoWindowComponent],
  providers: [DecimalPipe],
  templateUrl: './map.component.html',
  styleUrl: './map.component.css',
})
export class MapComponent implements OnInit {
  @Input() propertySummaries$ = new Observable<PropertySummary[]>();
  @Input() isDraggable: boolean = true;
  @Input() shouldFitBounds: boolean = true;
  // Set a callback to handle marker clicked on parent component
  @Output() propertyClicked: EventEmitter<PropertySummary> = new EventEmitter();
  @Output() scrollChanged: EventEmitter<google.maps.LatLngBounds> =
    new EventEmitter();
  //HTML GoogleMap this is to be used ngAfterViewInit
  //@ViewChild("propertyMap")
  map: google.maps.Map | undefined;
  private markersMap = new Map<string, marker>(); // Store markers persistently
  markers$: Observable<marker[]> = new Observable<marker[]>();
  bounds = new google.maps.LatLngBounds();
  mapSearch: boolean = true;

  public searchFilter: PropertyListFilterWithPagination | undefined;

  @ViewChild('cluster') _cluster!: MapMarkerClusterer;
  @ViewChild(MapInfoWindow) infoWindow!: MapInfoWindow;

  propSelectedId$ = new BehaviorSubject<string | undefined>(undefined);

  mapOptions: google.maps.MapOptions = {
    zoomControl: false,
    maxZoom: 20,
    draggable: true,
    disableDefaultUI: true, //removes default control icons. this is closer to the designed look.
    streetViewControl: false,
    center: new google.maps.LatLng(38.1169981, 21.6855135), //default map center Athens (when no properties available),
    zoom: 7,
  };
  infoWindowOptions: google.maps.InfoWindowOptions = {
    headerDisabled: true,

  };
  selectedMarker: marker | undefined;

  //style of marker's div.
  contentStyle: string = 'bg-primary rounded-full text-white p-2';
  private isSearch = false;
  private iconDefault: google.maps.Icon;
  private iconActive: google.maps.Icon;

  private prevNonPageParams: { [key: string]: string | null } | null = null;

  constructor(
    private destroyRef: DestroyRef,
    private route: ActivatedRoute,
    private propertyListingService: PropertyListingService,
    private findPropertyService: FindPropertyService
  ) {
    // ✅ Preload marker icons once
    this.iconDefault = {
      url: '/assets/pin-default.png',
      scaledSize: new google.maps.Size(120, 43),
      labelOrigin: new google.maps.Point(60, 17),
    };

    this.iconActive = {
      url: '/assets/pin-active.png',
      scaledSize: new google.maps.Size(120, 43),
      labelOrigin: new google.maps.Point(60, 17),
    };
  }
  private _backPressed: boolean = false;
  @HostListener('window:popstate', ['$event'])
  onPopState(event: any) {
    this._backPressed = true;
  }
  ngOnInit() {
    this.isSearch = true;

    combineLatest([
      this.findPropertyService.mapMustDebounce$,
      this.route.queryParamMap
    ]).subscribe(([mustDebounce, params]) => {
      this.shouldFitBounds =
        !params.has('searchFromMap') || params.get('searchFromMap') != 'true' || mustDebounce;
      // Extract all params except 'page'
      const nonPageParams: { [key: string]: string | null } = {};
      params.keys.forEach((key) => {
        if (key !== 'page') {
          nonPageParams[key] = params.get(key);
        }
      });

      // Compare with previous non-page params
      let shouldUpdate = true;
      if (this.prevNonPageParams) {
        const prev = this.prevNonPageParams;
        const curr = nonPageParams;
        const prevKeys = Object.keys(prev);
        const currKeys = Object.keys(curr);
        if (
          prevKeys.length === currKeys.length &&
          prevKeys.every((k) => prev[k] === curr[k])
        ) {
          // Only 'page' changed
          shouldUpdate = false;
        }
      }
      this.prevNonPageParams = { ...nonPageParams };

      if (!shouldUpdate) {
        return;
      }

      this.searchFilter =
        this.propertyListingService.getSearchFiltersFromUrl(params);

      const currentRouteIsProperties = this.route.snapshot.url
        .map((segment) => segment.path)
        .includes('properties');
      if (currentRouteIsProperties) {
        this.markers$ = combineLatest([
          this.propertyListingService.getPropertyListForMap(
            false,
            undefined,
            undefined,
            undefined,
            undefined,
            this.searchFilter
          ),
          this.propSelectedId$,
        ]).pipe(
          debounceTime(0),
          map(([properties, selectedId]) =>
            this.propertiesToMarkers(properties ?? [], selectedId)
          ),
          tap(([properties, selectedId]) => {
            if (this.bounds.isEmpty()) {
              this.bounds = new google.maps.LatLngBounds(
                new google.maps.LatLng(34.802075, 19.37439),
                new google.maps.LatLng(41.748877, 28.215021)
              );
            }
            if (this.shouldFitBounds || this._backPressed) {
              this.isSearch = this.shouldFitBounds ? true : false;
              console.log('Fitting bounds');
              this.map?.fitBounds(this.bounds);
              this._backPressed = false;
            }
          })
        );
      } else {
        this.markers$ = combineLatest([
          this.propertySummaries$,
          this.propSelectedId$,
        ]).pipe(
          debounceTime(0),
          map(([properties, selectedId]) =>
            this.propertySummariesToMarkers(properties ?? [], selectedId)
          ),
          tap(([properties, selectedId]) => {
            if (this.bounds.isEmpty()) {
              this.bounds = new google.maps.LatLngBounds(
                new google.maps.LatLng(34.802075, 19.37439),
                new google.maps.LatLng(41.748877, 28.215021)
              );
            }
            if (this.shouldFitBounds || this._backPressed) {
              this.isSearch = this.shouldFitBounds ? true : false;
              console.log('Fitting bounds');
              this.map?.fitBounds(this.bounds);
              this._backPressed = false;
            }
          })
        );
      }
    })

    this.mapOptions = { ...this.mapOptions, draggable: this.isDraggable };
  }
  zoomIn() {
    if (this.map) {
      this.map.setZoom(this.map.getZoom()! + 1);
    }
  }

  zoomOut() {
    if (this.map) {
      this.map.setZoom(this.map.getZoom()! - 1);
    }
  }

  propertySummariesToMarkers(
    properties: PropertySummary[],
    propSelectedId?: string
  ): marker[] {
    if (properties.length === 0) {
      return [];
    }

    this.bounds = new google.maps.LatLngBounds();

    return properties
      .filter((f) => f.address?.geoPoint != null)
      .map((p) => {
        let coordinates = p.address?.geoPoint?.split(',');
        let lat = Number.parseFloat(coordinates![0]);
        let lon = Number.parseFloat(coordinates![1]);

        let position = new google.maps.LatLng(lat, lon);
        let existingMarker = this.markersMap.get(p.id as string);

        if (!existingMarker) {
          existingMarker = new marker();
          this.markersMap.set(p.id as string, existingMarker);
        }
        existingMarker.id = p.id;
        existingMarker.position = position;
        // ✅ Use cached icons instead of fetching them again
        existingMarker.icon =
          propSelectedId === p.id ? this.iconActive : this.iconDefault;
        if (p.price) {
          existingMarker.title = {
            text: `€ ${p.price!.toLocaleString('el-GR')}`,
            color: 'white',
          };
          existingMarker.label = {
            text: `€ ${p.price!.toLocaleString('el-GR')}`,
            color: 'white',
            fontSize: '14px',
            fontWeight: 'bold',
          };
        } else {
          existingMarker.icon = {
            anchor: new google.maps.Point(54, 54),
            url: '../../../assets/PropertyPoint.png',
          };
        }

        this.bounds.extend(position);
        return existingMarker;
      });
  }

  propertiesToMarkers(
    properties: PropertyListForMapResponse,
    propSelectedId?: string
  ): marker[] {
    if (properties.properties && properties.properties.length === 0) {
      return [];
    }

    this.bounds = new google.maps.LatLngBounds();

    return properties
      .properties!.filter((f) => f.geoPoint != null)
      .map((p) => {
        let coordinates = p.geoPoint?.split(',');
        let lat = Number.parseFloat(coordinates![0]);
        let lon = Number.parseFloat(coordinates![1]);

        let position = new google.maps.LatLng(lat, lon);
        let existingMarker = this.markersMap.get(p.id as string);

        if (!existingMarker) {
          existingMarker = new marker();
          this.markersMap.set(p.id as string, existingMarker);
        }
        existingMarker.id = p.id;
        existingMarker.position = position;
        // ✅ Use cached icons instead of fetching them again
        existingMarker.icon =
          propSelectedId === p.id ? this.iconActive : this.iconDefault;
        if (p.price) {
          existingMarker.title = {
            text: `€ ${p.price!.toLocaleString('el-GR')}`,
            color: 'white',
          };
          existingMarker.label = {
            text: `€ ${p.price!.toLocaleString('el-GR')}`,
            color: 'white',
            fontSize: '14px',
            fontWeight: 'bold',
          };
        } else {
          existingMarker.icon = {
            anchor: new google.maps.Point(54, 54),
            url: '../../../assets/PropertyPoint.png',
          };
        }

        this.bounds.extend(position);
        return existingMarker;
      });
  }
  markerClicked(mrk: any, marker?: marker) {
    this.openInfoWindow(mrk, marker);

    this.propertySummaries$
      .pipe(takeUntilDestroyed(this.destroyRef), take(1))
      .subscribe((properties) => {
        let property = properties.find((f) => f.id == marker?.id);
        if (property) {
          this.propertyClicked.emit(property);
          this.propSelectedId$.next(property.id); // ✅ Update the selected marker reactively

          // ✅ Update only the clicked marker
          this.markersMap.forEach((marker, key) => {
            marker.icon =
              key === marker?.id ? this.iconActive : this.iconDefault;
          });
        }
      });
  }

  onMapClick() {
    this.selectedMarker = undefined;
    this.propertyClicked.emit();
    this.propSelectedId$.next(undefined);
    this.infoWindow.close();
  }

  private openInfoWindow(mrk: any, marker?: marker) {
    this.selectedMarker = marker;
    this.isSearch = true;
    setTimeout(() => {
      this.infoWindow.open(mrk);
    }, 200);
  }

  mapInitialized(map: google.maps.Map) {
    this.map = map;
    this.map!.addListener('idle', () => {
      this._cluster.markerClusterer?.repaint();
      if (!this.isSearch) {
        this._cluster.markerClusterer?.clearMarkers();
        this.scrollChanged.emit(this.map?.getBounds());
      }
      this.isSearch = false;
    });
  }
}
